import React, { useCallback, useEffect, useRef, useState } from 'react';

import { di } from 'react-magnetic-di';
import { useDebouncedCallback } from 'use-debounce';

import { cssMap } from '@atlaskit/css';
import ProjectIcon from '@atlaskit/icon/core/project';
import Image from '@atlaskit/image';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Select, {
	components,
	type ControlProps,
	type OptionProps,
	type OptionType,
	type SingleValueProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

type ProjectOptionType = OptionType & { iconUrl?: string };

const styles = cssMap({
	customControl: {
		width: '100%',
	},
	projectIcon: {
		flexShrink: 0,
		borderRadius: token('radius.xsmall'),
		width: '16px',
		height: '16px',
	},
});

const DEBOUNCE_DELAY = 300;

const extractProjectFromARI = (input: string): string => {
	// Match the last number digits after "project/"
	const pattern = /project\/(\d+)$/;
	const match = input.match(pattern);
	return match ? match[1] : '';
};

export const GetProjectQuery = `
query GetProject($projectAri: ID!) {
  jira {
    jiraProject(id: $projectAri) {
	  id
	  projectId
      name
	  avatar {
		xsmall
	  }
    }
  }
}`;

export const SearchJiraProjectsQuery = `
query GetProjects($query: String!, $siteAri: String!) {
  	search {
		search(
			query: $query
			filters: {
				entities: ["ati:cloud:jira:project"],
				locations: [$siteAri],
        jiraFilters: {
          projectFilter : {
            projectTypes : software
          }
        }
			}
			experience: "rovo-chat"
		) {
			edges {
				node {
					... on SearchResultJiraProject {
						id
						title
						iconUrl
						url

					}
				}
			}
		}
	}
}
`;

type RawProjectData = {
	id: string;
	projectId: string;
	name: string;
	avatar?: {
		xsmall?: string;
	};
};

type GetProjectResponse = {
	jira: {
		jiraProject: RawProjectData;
	};
};

type SearchJiraProjectsResponse = {
	search: {
		search: {
			edges: {
				node: {
					id: string;
					title: string;
					iconUrl: string;
					url: string;
				};
			}[];
		};
	};
};

type ProjectSelectorProps = {
	initialProjectId?: string;
	siteId: string;
	onProjectChangeSelect: ({
		projectId,
		projectName,
		iconUrl,
	}: {
		projectId: string;
		projectName: string;
		iconUrl?: string;
	}) => void;
	selectedProject?: ProjectOptionType;
	isDisabled: boolean;
};

export const ProjectSelector = ({
	siteId,
	onProjectChangeSelect,
	initialProjectId,
	selectedProject,
	isDisabled,
}: ProjectSelectorProps): React.JSX.Element => {
	di(performGqlFetch);

	const { sendAnalyticsEvent } = useAnalytics();
	const [allOptions, setAllOptions] = useState<ProjectOptionType[]>([]);
	const [displayedOptions, setDisplayedOptions] = useState<ProjectOptionType[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const hasFetchedInitialProject = useRef(false);

	const sortOptionsWithSelectedFirst = useCallback(
		(options: ProjectOptionType[]): ProjectOptionType[] => {
			if (!selectedProject?.value || !options || options.length === 0) {
				return options || [];
			}

			const otherOptions = options.filter((option) => {
				let optionValue = option.value;
				if (typeof option.value === 'string') {
					optionValue = extractProjectFromARI(option.value) || option.value;
				}
				return optionValue !== selectedProject.value;
			});

			return [selectedProject, ...otherOptions];
		},
		[selectedProject],
	);

	useEffect(() => {
		if (!initialProjectId || hasFetchedInitialProject.current) {
			return;
		}
		hasFetchedInitialProject.current = true;

		(async () => {
			try {
				const {
					data: {
						jira: { jiraProject },
					},
				} = await performGqlFetch<GetProjectResponse, { projectAri: string }>({
					gqlQuery: GetProjectQuery,
					variables: {
						projectAri: `ari:cloud:jira:${siteId}:project/${initialProjectId}`,
					},
				});

				onProjectChangeSelect({
					projectId: jiraProject.projectId,
					projectName: jiraProject.name,
					iconUrl: jiraProject.avatar?.xsmall,
				});
			} catch (error) {
				logException({
					exception: error,
					name: 'GetProjectQueryError',
					tags: { cloudId: siteId },
				});
			}
		})();
	}, [initialProjectId, siteId, onProjectChangeSelect]);

	const loadOptions = useCallback(
		async (value: string): Promise<ProjectOptionType[]> => {
			setIsLoading(true);
			try {
				const response = await performGqlFetch<
					SearchJiraProjectsResponse,
					{
						query: string;
						siteAri: string;
					}
				>({
					gqlQuery: SearchJiraProjectsQuery,
					variables: {
						query: value,
						siteAri: `ari:cloud:jira::site/${siteId}`,
					},
				});

				return (
					response.data?.search?.search?.edges
						?.map((edge) => edge.node)
						?.map((item) => ({
							label: item.title,
							value: item.id,
							iconUrl: item.iconUrl,
						})) ?? []
				);
			} catch (error) {
				logException({
					exception: error,
					name: 'SearchJiraProjectsQueryError',
					tags: { cloudId: siteId },
				});
				return [];
			} finally {
				setIsLoading(false);
			}
		},
		[siteId],
	);

	// Preload initial options when component mounts
	useEffect(() => {
		const preloadOptions = async () => {
			if (allOptions.length === 0 && !isLoading) {
				const initialOptions = await loadOptions('');
				setAllOptions(initialOptions);
				setDisplayedOptions(initialOptions);
			}
		};

		preloadOptions();
	}, [allOptions.length, isLoading, loadOptions]);

	const handleProjectChange = useCallback(
		(selectedOption: ProjectOptionType | null) => {
			const selectedOptionValue = selectedOption?.value;
			if (!selectedOption || typeof selectedOptionValue !== 'string') {
				return;
			}

			const selectedOptionProjectId =
				extractProjectFromARI(selectedOptionValue) || selectedOptionValue;
			if (selectedOptionProjectId === selectedProject?.value) {
				return;
			}

			onProjectChangeSelect({
				projectId: selectedOptionProjectId,
				projectName: selectedOption.label ?? '',
				iconUrl: selectedOption.iconUrl,
			});

			if (!allOptions.some((option) => option.value === selectedOptionValue)) {
				setAllOptions((prev) => [...prev, selectedOption]);
			}
			setDisplayedOptions(allOptions);
		},
		[selectedProject, onProjectChangeSelect, allOptions],
	);

	const [debouncedLoadOptions] = useDebouncedCallback(async (value: string) => {
		setDisplayedOptions(await loadOptions(value));
	}, DEBOUNCE_DELAY);

	const handleInputChange = useCallback(
		(inputValue: string, { action }: { action: string }) => {
			if (action === 'input-change') {
				debouncedLoadOptions(inputValue);
			}
		},
		[debouncedLoadOptions],
	);

	return (
		<Select<ProjectOptionType>
			testId="jira-version-create-project-selector"
			inputId="jira-version-create-project-selector-input"
			label="Select a project"
			isDisabled={isDisabled}
			spacing="compact"
			appearance="default"
			value={selectedProject}
			isLoading={isLoading}
			options={sortOptionsWithSelectedFirst(displayedOptions)}
			onChange={handleProjectChange}
			onInputChange={handleInputChange}
			onMenuOpen={() => {
				sendAnalyticsEvent({
					action: 'opened',
					actionSubject: 'dropdown',
					actionSubjectId: 'rovoJiraVersionCreateProjectSelector',
				});
			}}
			onMenuClose={() => {
				setDisplayedOptions(allOptions);
			}}
			components={{
				Control: CustomControl,
				Option: CustomOption,
				SingleValue: CustomSingleValue,
			}}
		/>
	);
};

const ProjectIconImage = ({ iconUrl }: { iconUrl?: string }) => {
	if (!iconUrl) {
		return <ProjectIcon label="" size="small" />;
	}

	return (
		<Box xcss={styles.projectIcon}>
			<Image src={iconUrl} alt="" />
		</Box>
	);
};

const CustomOption = ({ children, ...props }: OptionProps<ProjectOptionType>) => {
	return (
		<components.Option {...props}>
			<Inline alignBlock="center" space="space.100">
				<ProjectIconImage iconUrl={props.data.iconUrl} />
				{children}
			</Inline>
		</components.Option>
	);
};

const CustomSingleValue = ({ children, ...props }: SingleValueProps<ProjectOptionType>) => {
	return (
		<components.SingleValue {...props}>
			<Inline alignBlock="center" space="space.075">
				{children}
			</Inline>
		</components.SingleValue>
	);
};

const CustomControl = ({ children, ...props }: ControlProps<ProjectOptionType>) => {
	const selectedOption = props.getValue()?.[0] as ProjectOptionType | undefined;
	return (
		<components.Control {...props}>
			<Box paddingInline="space.100" xcss={styles.customControl}>
				<Inline alignBlock="center">
					<ProjectIconImage iconUrl={selectedOption?.iconUrl} />
					{children}
				</Inline>
			</Box>
		</components.Control>
	);
};
